import { useEffect } from 'react'
import { Canvas, Text } from 'fabric'
import { clampFontSize } from '../../utils/validation'
import { convertHexWithAlphaToRgba } from '../../utils/color/colorConversion'
import { applyTextBorderStyles } from '../../utils/text/textObject'
import {
    TEXT_OBJECT_PADDING,
    TEXT_OBJECT_BACKGROUND_BORDER_RADIUS,
} from '../../constants'
import { applyOptimalRenderingSettings } from '../utils/canvasUtils'
import type { FontFamily } from '../../types'
import type { MediaDimensions } from '../types'

/**
 * Параметры для хука обновления размера шрифта
 */
export interface UseTextObjectFontSizeUpdateParams {
    fontSize: number
    fontFamily: FontFamily
    hasBackground: boolean
    backgroundColor: string | null
    fabricCanvasRef: React.MutableRefObject<Canvas | null>
    textObjectRef: React.MutableRefObject<Text | null>
    isScalingFromSliderRef: React.MutableRefObject<boolean>
    handleTextScaling: () => void
    safeArea: {
        top: number
        bottom: number
        left: number
        right: number
    }
    mediaDimensions: MediaDimensions | null // Размеры медиа для проверки готовности
}

/**
 * Хук для обновления размера шрифта текстового объекта (синхронизация с ползунком)
 */
export function useTextObjectFontSizeUpdate(
    params: UseTextObjectFontSizeUpdateParams
): void {
    const {
        fontSize,
        fontFamily,
        hasBackground,
        backgroundColor,
        fabricCanvasRef,
        textObjectRef,
        isScalingFromSliderRef,
        handleTextScaling,
        safeArea,
        mediaDimensions,
    } = params

    useEffect(() => {
        if (!fabricCanvasRef.current || !textObjectRef.current) {
            return
        }

        // Не обновляем размер шрифта, если медиа еще не загружено
        // Это предотвращает дергания и некорректные расчеты при первом открытии
        if (!mediaDimensions) {
            return
        }

        const textObj = textObjectRef.current
        const fabricCanvas = fabricCanvasRef.current

        // Временно отключаем обработчик scaling
        textObj.off('scaling', handleTextScaling)
        isScalingFromSliderRef.current = true

        // Рассчитываем максимальную ширину текста с учетом padding
        const canvasWidth = fabricCanvas.width
        const maxWidth = calculateTextMaxWidth(
            canvasWidth,
            hasBackground,
            backgroundColor
        )

        // Определяем, является ли шрифт проблемным (Inter, Montserrat)
        const isKerningSensitiveFont =
            fontFamily === 'Inter' || fontFamily === 'Montserrat'

        // Подготавливаем параметры подложки
        const rgbaBackgroundColor = hasBackground && backgroundColor
            ? convertHexWithAlphaToRgba(backgroundColor)
            : null

        // Объединяем все изменения в один батч set() для плавности
        const updates: Parameters<typeof textObj.set>[0] = {
            fontSize: clampFontSize(fontSize),
            width: maxWidth,
            scaleX: 1,
            scaleY: 1,
            // Применяем charSpacing для проблемных шрифтов сразу
            charSpacing: isKerningSensitiveFont ? -15 : 0,
        }

        // Добавляем параметры подложки, если нужно
        if (hasBackground && rgbaBackgroundColor) {
            updates.textBackgroundColor = rgbaBackgroundColor
            updates.textBackgroundPadding = TEXT_OBJECT_PADDING.BACKGROUND
            updates.textBackgroundBorderRadius = TEXT_OBJECT_BACKGROUND_BORDER_RADIUS
        } else {
            updates.textBackgroundColor = undefined
            updates.textBackgroundPadding = undefined
            updates.textBackgroundBorderRadius = undefined
        }

        // Применяем все изменения одним батчем
        textObj.set(updates)

        // Пересчитываем размеры текста
        textObj.initDimensions()

        // Обновляем настройки рендеринга canvas для оптимальной четкости текста
        // Для маленьких шрифтов используем более резкие настройки
        const canvasElement = fabricCanvas.getElement()
        if (canvasElement) {
            const ctx = canvasElement.getContext('2d')
            if (ctx) {
                applyOptimalRenderingSettings(ctx, fontSize)
            }
        }

        // Применяем настройки обводки (это отдельный set, но он легковесный)
        applyTextBorderStyles(textObj)

        // Используем requestAnimationFrame для плавного рендера
        // Это синхронизирует перерисовку с циклом браузера и делает анимацию плавнее
        requestAnimationFrame(() => {
            if (!textObj || !fabricCanvas.getObjects().includes(textObj)) {
                return
            }

            // Для проблемных шрифтов делаем дополнительный пересчёт
            // Inter и Montserrat требуют более агрессивной обработки из-за особенностей кернинга
            if (isKerningSensitiveFont) {
                // Принудительно применяем charSpacing для проблемных шрифтов
                // Это важно, так как charSpacing может быть сброшен при обновлении объекта
                textObj.set({ charSpacing: -15 })
                
                // Пересчитываем размеры после применения шрифта
                // Это гарантирует, что размеры будут пересчитаны после применения charSpacing
                textObj.initDimensions()

                // Применяем подложку ПОСЛЕ пересчета размеров
                // Это важно для правильного позиционирования подложки
                if (hasBackground && rgbaBackgroundColor) {
                    textObj.set({
                        textBackgroundColor: rgbaBackgroundColor,
                        textBackgroundPadding: TEXT_OBJECT_PADDING.BACKGROUND,
                        textBackgroundBorderRadius:
                            TEXT_OBJECT_BACKGROUND_BORDER_RADIUS,
                    })
                }

                // Дополнительный пересчет в следующем кадре для гарантии правильного рендера
                // Это особенно важно для Inter, который имеет проблемы с measureText()
                requestAnimationFrame(() => {
                    if (!textObj || !fabricCanvas.getObjects().includes(textObj)) {
                        return
                    }

                    // Принудительно применяем charSpacing еще раз
                    textObj.set({ charSpacing: -15 })
                    textObj.initDimensions()

                    // Применяем подложку, если нужно
                    if (hasBackground && rgbaBackgroundColor) {
                        textObj.set({
                            textBackgroundColor: rgbaBackgroundColor,
                            textBackgroundPadding: TEXT_OBJECT_PADDING.BACKGROUND,
                            textBackgroundBorderRadius:
                                TEXT_OBJECT_BACKGROUND_BORDER_RADIUS,
                        })
                    }

                    // Пересчитываем координаты и перерисовываем
                    textObj.setCoords()
                    fabricCanvas.renderAll()
                })
            } else {
                // Для обычных шрифтов просто пересчитываем координаты и перерисовываем
                textObj.setCoords()
                fabricCanvas.renderAll()
            }
        })

        // Включаем обработчик обратно
        isScalingFromSliderRef.current = false
        textObj.on('scaling', handleTextScaling)
    }, [
        fontSize,
        fontFamily,
        hasBackground,
        backgroundColor,
        handleTextScaling,
        fabricCanvasRef,
        textObjectRef,
        isScalingFromSliderRef,
        safeArea,
        mediaDimensions,
    ])
}

/**
 * Расчет максимальной ширины текста с учетом padding
 */
function calculateTextMaxWidth(
    canvasWidth: number,
    hasBackground: boolean,
    backgroundColor: string | null
): number {
    const borderPadding = TEXT_OBJECT_PADDING.BORDER * 2
    const backgroundPadding =
        hasBackground && backgroundColor !== null
            ? TEXT_OBJECT_PADDING.BACKGROUND * 2
            : 0
    return Math.max(
        TEXT_OBJECT_PADDING.MIN_WIDTH,
        canvasWidth - borderPadding - backgroundPadding
    )
}

